import classNames from "classnames";
import { formatDistanceToNow } from "date-fns";
import { Tooltip } from "primereact/tooltip";
import { useId, useMemo } from "react";
import { ReportStatus } from "../../../../api/types";
import type {
  ClearanceCheck,
  ClearanceCheckStatus,
  ClearanceReview,
  FixSovRunSummary,
} from "../../../../client";
import { useFeatureEnabled } from "../../../../IfFeatureEnabled";
import { useLocalUser } from "../../data";
import { useReportJobs } from "../../rater/useIntegrationJobs";
import { latestJobByType } from "../IntegrationJobsCell";
import { StatusIcon } from "../StatusIcon";
import styles from "./ClearanceStatusHover.module.css";
import {
  clearanceBadge,
  fixSovHeadline,
  PRE_CLEARANCE_STATUSES,
  StageState,
  stageState,
} from "./clearanceStatus";
import { useClearanceMetadata } from "./useClearanceMetadata";

const CHECK_ICON: Record<ClearanceCheckStatus, string> = {
  pass: "pi pi-check",
  fail: "pi pi-times",
  unknown: "pi pi-question",
};

const timeAgo = (iso: string) =>
  formatDistanceToNow(new Date(iso), { addSuffix: true });

const StageRow = ({
  label,
  state,
  children,
}: {
  label: string;
  state: StageState;
  children?: React.ReactNode;
}) => (
  <div className={styles.stage}>
    <div className={styles.stageHeader}>
      <span className={styles.stageLabel}>{label}</span>
      {state.kind === "in_progress" && (
        <span className={classNames(styles.stageState, styles.running)}>
          <span className={styles.pulse} />
          Running
        </span>
      )}
      {state.kind === "not_run" && (
        <span className={classNames(styles.stageState, styles.muted)}>
          Not run
        </span>
      )}
    </div>
    {state.kind === "done" && children}
  </div>
);

const FixSovDetail = ({ summary }: { summary: FixSovRunSummary }) => {
  const failed = !summary.completed;
  const facts = [
    summary.num_turns !== null ? `${summary.num_turns} turns` : null,
    summary.cost_usd !== null ? `$${summary.cost_usd.toFixed(2)}` : null,
    timeAgo(summary.finished_at),
  ].filter((f): f is string => f !== null);
  return (
    <>
      <div className={classNames(styles.line, failed && styles.bad)}>
        <i
          className={classNames(
            styles.checkIcon,
            failed ? "pi pi-times" : "pi pi-check"
          )}
        />
        <span className={styles.lineText}>{fixSovHeadline(summary)}</span>
      </div>
      <div className={styles.facts}>{facts.join(" · ")}</div>
      {summary.error && <div className={styles.error}>{summary.error}</div>}
      {summary.tool_errors.length > 0 && (
        <div className={styles.error}>
          Tool errors: {summary.tool_errors.join(", ")}
        </div>
      )}
      {summary.summary && <div className={styles.prose}>{summary.summary}</div>}
    </>
  );
};

const CheckRow = ({ check }: { check: ClearanceCheck }) => (
  <div
    className={classNames(styles.line, {
      [styles.bad]: check.status === "fail",
      [styles.unsure]: check.status === "unknown",
    })}
  >
    <i className={classNames(styles.checkIcon, CHECK_ICON[check.status])} />
    <div className={styles.lineText}>
      <span className={styles.checkLabel}>{check.label}</span>
      <span className={styles.checkSummary}>{check.summary}</span>
      {check.details && check.details.length > 0 && (
        <ul className={styles.checkDetails}>
          {check.details.map((detail, i) => (
            <li key={i}>{detail}</li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const ReviewDetail = ({ review }: { review: ClearanceReview }) => {
  if (review.outcome === "skipped") {
    return (
      <div className={classNames(styles.line, styles.unsure)}>
        <i className={classNames(styles.checkIcon, "pi pi-minus")} />
        <span className={styles.lineText}>
          Skipped: {review.skip_reason ?? "no reason recorded"}
        </span>
        <span className={styles.facts}>{timeAgo(review.finished_at)}</span>
      </div>
    );
  }
  return (
    <>
      {review.checks.map((check) => (
        <CheckRow key={check.id} check={check} />
      ))}
      <div className={styles.facts}>{timeAgo(review.finished_at)}</div>
    </>
  );
};

/**
 * The dashboard's status cell. Where "automated-clearance-status" is enabled, a
 * submission that has not been cleared yet also carries a badge for the
 * automated clearance work (SOV fix, then the general review) and a hover panel
 * with the details behind it. Job progress comes from the report-jobs poll;
 * results from the stored metadata.
 */
export const ClearanceStatusHover = ({
  reportId,
  status,
}: {
  reportId: string;
  status: ReportStatus;
}) => {
  const clearanceStatusEnabled = useFeatureEnabled(
    "automated-clearance-status",
    undefined,
    useLocalUser()
  );
  const active = clearanceStatusEnabled && PRE_CLEARANCE_STATUSES.has(status);
  const targetId = `clearance-${useId().replace(/:/g, "")}`;

  const { data: entry } = useClearanceMetadata(reportId, active);
  const { jobs } = useReportJobs(reportId);
  const latestByType = useMemo(() => latestJobByType(jobs), [jobs]);

  if (!active) return <StatusIcon status={status} />;

  const fixSov = stageState(latestByType["FIX_SOV"], entry?.fix_sov);
  const review = stageState(latestByType["CLEARANCE"], entry?.review);
  const badge = clearanceBadge(fixSov, review, entry);

  return (
    <>
      <Tooltip
        target={`#${targetId}`}
        position="right"
        showDelay={300}
        className={styles.tooltip}
      >
        <div className={styles.panel}>
          <div className={styles.title}>Automated clearance</div>
          <StageRow label="Fix SOV" state={fixSov}>
            {entry?.fix_sov && <FixSovDetail summary={entry.fix_sov} />}
          </StageRow>
          <StageRow label="Review" state={review}>
            {entry?.review && <ReviewDetail review={entry.review} />}
          </StageRow>
        </div>
      </Tooltip>
      <div id={targetId} className={styles.target}>
        <StatusIcon status={status} />
        {badge && (
          <span className={classNames(styles.badge, styles[badge.tone])}>
            {badge.tone === "running" && <span className={styles.pulse} />}
            {badge.label}
          </span>
        )}
      </div>
    </>
  );
};
